import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import useDismiss from '../../hooks/useDismiss';
import ThemeToggle, { ThemeMenuItems } from './ThemeToggle';
import '../ui/ui.css';
import './TopBar.css';

export const NAV_PLACES = [
    { id: 'inventory', label: 'Inventory', to: '/' },
    { id: 'branches', label: 'Branches', to: '/branches' },
    { id: 'tasks', label: 'Tasks', to: '/tasks' },
];

export const BYLINE = 'Predictive Pricing for Equipment Dealers';

const RefreshIcon = ({ spinning }) => (
    <svg className={spinning ? 'v2-spin' : undefined} width="14" height="14" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" focusable="false">
        <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
    </svg>
);

RefreshIcon.propTypes = { spinning: PropTypes.bool };

function UserMenu({
    name, email, lastUpdated, onSignOut, onPreferences,
}) {
    const menuId = `${useId().replace(/:/g, '')}-user-menu`;
    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);
    const buttonRef = useRef(null);
    const firstItemRef = useRef(null);
    const close = useCallback(() => setOpen(false), []);
    useDismiss(wrapRef, close, open);

    useEffect(() => {
        if (open) firstItemRef.current?.focus();
    }, [open]);

    const onMenuKeyDown = (e) => {
        if (e.key === 'Escape') {
            e.stopPropagation();
            setOpen(false);
            buttonRef.current?.focus();
        }
    };

    return (
        <div className="v2-usermenu" ref={wrapRef}>
            <button
                ref={buttonRef}
                type="button"
                className="v2-tb-btn"
                aria-haspopup="menu"
                aria-expanded={open}
                aria-controls={open ? menuId : undefined}
                onClick={() => setOpen((v) => !v)}
            >
                <span className="v2-avatar" aria-hidden="true">{(name || 'U')[0].toUpperCase()}</span>
                <span className="v2-tb-name">{name}</span>
            </button>
            {open && (
                <div className="v2-menu" id={menuId} role="menu" aria-label="Account" onKeyDown={onMenuKeyDown}>
                    <div className="v2-menu-who">
                        <b>{name}</b>
                        {email && email !== name && <span>{email}</span>}
                    </div>
                    {lastUpdated && (
                        <div className="v2-menu-info">
                            <span>Last updated</span>
                            <span>{lastUpdated}</span>
                        </div>
                    )}
                    {/* QA P-5: My preferences first, then the theme (narrow screens), Sign out last. */}
                    {onPreferences && <div className="v2-menu-sep" role="separator" />}
                    {onPreferences && (
                        <button
                            ref={firstItemRef}
                            type="button"
                            role="menuitem"
                            className="v2-menu-item v2-menu-item--plain"
                            onClick={() => { setOpen(false); onPreferences(); }}
                        >
                            <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14" aria-hidden="true" focusable="false">
                                <path d="M5 4a1 1 0 00-2 0v7.268a2 2 0 000 3.464V16a1 1 0 102 0v-1.268a2 2 0 000-3.464V4zM11 4a1 1 0 10-2 0v1.268a2 2 0 000 3.464V16a1 1 0 102 0V8.732a2 2 0 000-3.464V4zM16 3a1 1 0 011 1v7.268a2 2 0 010 3.464V16a1 1 0 11-2 0v-1.268a2 2 0 010-3.464V4a1 1 0 011-1z" />
                            </svg>
                            My preferences
                        </button>
                    )}
                    {/* On narrow screens the top bar's theme button is
                        hidden; the choice lives here instead (theme.css). */}
                    <div className="v2-menu-theme">
                        <div className="v2-menu-sep" role="separator" />
                        <span className="v2-menu-theme-head" aria-hidden="true">Theme</span>
                        <ThemeMenuItems />
                    </div>
                    <div className="v2-menu-sep" role="separator" />
                    <button ref={onPreferences ? undefined : firstItemRef} type="button" role="menuitem" className="v2-menu-item" onClick={onSignOut}>
                        <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14" aria-hidden="true" focusable="false">
                            <path fillRule="evenodd" d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z" clipRule="evenodd" />
                        </svg>
                        Sign out
                    </button>
                </div>
            )}
        </div>
    );
}

UserMenu.propTypes = {
    name: PropTypes.string.isRequired,
    email: PropTypes.string,
    lastUpdated: PropTypes.string,
    onSignOut: PropTypes.func.isRequired,
    onPreferences: PropTypes.func,
};

/**
 * The redesigned top bar (prototype `.topbar`), presentational. AppTopBar
 * feeds it from the app's real state; the UI kit feeds it sample props.
 */
function TopBar({
    dealership, dealershipTitle = 'Dealership currently in view', scopeLabel,
    readOnly = false, canSwitch = false, onSwitch,
    cacheStatus, current, onNavigate, isAdmin = false,
    showRefresh = false, onRefresh, refreshing = false,
    userName, userEmail, onSignOut, onPreferences,
}) {
    const navClick = (to) => (onNavigate ? (e) => onNavigate(e, to) : undefined);
    return (
        <header className="v2-topbar" role="banner">
            <div className="v2-brand">
                <img src="/ironsight-wordmark.png" alt="IronSight" className="v2-brand-mark" />
                <span className="v2-byline">{BYLINE}</span>
            </div>
            {(dealership || canSwitch || scopeLabel) && (
                <span className="v2-org">
                    {scopeLabel && <span className="v2-scope">{scopeLabel}</span>}
                    {dealership && <span title={dealershipTitle}>{dealership}</span>}
                    {readOnly && (
                        <span className="v2-readonly" title="You can look at this dealership's data but not change it">
                            <span className="v2-readonly-dot" aria-hidden="true" />
                            Read-only
                        </span>
                    )}
                    {canSwitch && (
                        <button type="button" className="v2-org-switch" onClick={onSwitch} title="Look at a different dealership">
                            Switch dealership
                        </button>
                    )}
                </span>
            )}
            <span className="v2-tb-spacer" />
            {cacheStatus && (
                <span className={`v2-updated${cacheStatus.stale ? ' is-stale' : ''}`} title={cacheStatus.title}>
                    Updated {cacheStatus.label}
                </span>
            )}
            <nav className="v2-nav" aria-label="Main">
                {NAV_PLACES.map((p) => (
                    <Link
                        key={p.id}
                        to={p.to}
                        aria-current={current === p.id ? 'page' : undefined}
                        onClick={navClick(p.to)}
                    >
                        {p.label}
                    </Link>
                ))}
            </nav>
            {isAdmin && (
                <Link
                    to="/admin"
                    className="v2-tb-btn"
                    title="Switch to the admin console"
                    aria-current={current === 'admin' ? 'page' : undefined}
                >
                    Admin
                </Link>
            )}
            {showRefresh && (
                <button
                    type="button"
                    className="v2-tb-btn"
                    title="Refresh Data"
                    aria-busy={refreshing}
                    onClick={onRefresh}
                >
                    <RefreshIcon spinning={refreshing} />
                    Refresh
                </button>
            )}
            <ThemeToggle />
            <UserMenu name={userName} email={userEmail} lastUpdated={cacheStatus?.time} onSignOut={onSignOut} onPreferences={onPreferences} />
        </header>
    );
}

TopBar.propTypes = {
    dealership: PropTypes.string,
    dealershipTitle: PropTypes.string,
    scopeLabel: PropTypes.string,
    readOnly: PropTypes.bool,
    canSwitch: PropTypes.bool,
    onSwitch: PropTypes.func,
    cacheStatus: PropTypes.shape({
        label: PropTypes.string.isRequired,
        stale: PropTypes.bool,
        title: PropTypes.string,
        time: PropTypes.string,
    }),
    current: PropTypes.oneOf(['inventory', 'branches', 'tasks', 'admin', null]),
    onNavigate: PropTypes.func,
    isAdmin: PropTypes.bool,
    showRefresh: PropTypes.bool,
    onRefresh: PropTypes.func,
    refreshing: PropTypes.bool,
    userName: PropTypes.string.isRequired,
    userEmail: PropTypes.string,
    onSignOut: PropTypes.func.isRequired,
    onPreferences: PropTypes.func,
};

export default TopBar;
